// src/app/components/ValidationDecisionCommentsSection.tsx

import React, { useMemo } from "react";
import { Box } from "@mantine/core";
import type {
  IRoom,
  IRoomValidationDecisions,
  IRoomValidationStatusState,
  IComment,
} from "@backend/src/schemas";
import { useAppSelector } from "../../app/hooks";
import { CommentsSection } from "../../components/comments/CommentsSection";
import { selectProjectNo } from "../../projectSelectors";
import { selectHttpSingleRoomFull } from "../../Room/roomSelectors";
import {
  usePostMultiplacerRoomValidationDecisionCommentDeleteMutation,
  usePostMultiplacerRoomValidationDecisionCommentMutation,
  usePostMultiplacerRoomValidationDecisionCommentUpdateMutation,
} from "../../../openapi";
import { selectHttpCurrentUser } from "../../globalSelectors";

type Props = {
  /**
   * Key på formen "<validationKey>.<discipline>"
   * f.eks. "standardRoom.E", "standardRoom.V", "rie.RIV" osv.
   */
  validationDecisionKey: string;
};

export const ValidationDecisionCommentsSection: React.FC<Props> = ({ validationDecisionKey }) => {
  const projectNo = useAppSelector(selectProjectNo)!;
  const room = useAppSelector(selectHttpSingleRoomFull) as IRoom;
  const currentUserId = useAppSelector(selectHttpCurrentUser)!._id as string;

  const [addCommentMutation, { isLoading: isAdding }] =
    usePostMultiplacerRoomValidationDecisionCommentMutation();
  const [updateCommentMutation, { isLoading: isUpdating }] =
    usePostMultiplacerRoomValidationDecisionCommentUpdateMutation();
  const [deleteCommentMutation, { isLoading: isDeleting }] =
    usePostMultiplacerRoomValidationDecisionCommentDeleteMutation();

  const isSubmitting = isAdding || isUpdating || isDeleting;

  // Split nyckeln: "standardRoom.E" → ["standardRoom", "E"]
  const [validationKey, discipline] = useMemo(() => {
    const parts = validationDecisionKey.split(".");
    return [parts[0], parts[1]] as [string | undefined, string | undefined];
  }, [validationDecisionKey]);

  const comments: IComment[] = useMemo(() => {
    if (!validationKey || !discipline) return [];

    const decisions: IRoomValidationDecisions | undefined = room.validationDecisions;
    if (!decisions) return [];

    const record = (decisions as any)[validationKey] as
      | Record<string, IRoomValidationStatusState>
      | undefined;

    if (!record) return [];

    return record[discipline]?.comments ?? [];
  }, [room, validationKey, discipline]);

  return (
    <Box mt="sm">
      <CommentsSection
        title="Kommentarer"
        comments={comments}
        isLoading={false}
        currentUserId={currentUserId}
        isSubmitting={isSubmitting}
        onCreateComment={async (message) => {
          await addCommentMutation({
            body: {
              projectNo,
              roomNumber: room.roomNumber,
              decisionKey: validationDecisionKey,
              message,
            },
          });
        }}
        onEditComment={async (commentId, newMessage) => {
          await updateCommentMutation({
            body: {
              projectNo,
              roomNumber: room.roomNumber,
              decisionKey: validationDecisionKey,
              commentId,
              message: newMessage,
            },
          });
        }}
        onDeleteComment={async (commentId) => {
          await deleteCommentMutation({
            body: {
              projectNo,
              roomNumber: room.roomNumber,
              decisionKey: validationDecisionKey,
              commentId,
            },
          });
        }}
      />
    </Box>
  );
};
